import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { ClientConfig, ClientFeatures, WatermarkConfig, HighlightsConfig } from '@/types/client-config'
import { apiClient } from '@/lib/api-client'
import { isApiMocked } from '@/lib/env'
import mockSeasons from './data/seasons.json'

export interface Season {
  id: string
  label: string
}

export const useConfigStore = defineStore('config', () => {
  const config = ref<ClientConfig | null>(null)
  const seasons = ref<Season[]>(isApiMocked ? (mockSeasons as Season[]) : [])

  function initialize(clientConfig: ClientConfig) {
    config.value = clientConfig
  }

  async function fetchSeasons(): Promise<void> {
    try {
      if (isApiMocked) {
        seasons.value = mockSeasons
      } else {
        seasons.value = await apiClient.get<Season[]>('/seasons')
      }
      const seasonIds = new Set(seasons.value.map((s) => s.id))
      for (const key of Object.keys(config.value?.seasonSubjectAvailability ?? {})) {
        if (!seasonIds.has(key)) {
          console.warn(`[config] seasonSubjectAvailability references unknown season: ${key}`)
        }
      }
    } catch (err) {
      console.warn('[config] Failed to fetch seasons:', err)
    }
  }

  const branding = computed(() => config.value!.branding)
  const subjects = computed(() => config.value!.subjects)
  const features = computed(() => config.value!.features)
  const links = computed(() => config.value!.links)
  const locale = computed(() => config.value!.locale)
  const WATERMARK_DEFAULTS: Required<WatermarkConfig> = {
    format: 'name',
    opacity: 0.06,
    spacing: 1,
    fontSize: '0.875rem',
  }

  const watermarkConfig = computed(() => ({
    ...WATERMARK_DEFAULTS,
    ...config.value?.watermarkConfig,
  }))

  const HIGHLIGHTS_DEFAULTS: Required<HighlightsConfig> = { maxPerQuestion: 20 }

  const highlightsConfig = computed(() => ({
    ...HIGHLIGHTS_DEFAULTS,
    ...config.value?.highlightsConfig,
  }))

  const seasonSubjectAvailability = computed(
    () => config.value?.seasonSubjectAvailability ?? {},
  )

  const subjectOptions = computed(() =>
    subjects.value.map((s) => ({ value: s.key, label: s.label })),
  )

  const seasonOptions = computed(() =>
    seasons.value.map((s) => ({ value: s.id, label: s.label })),
  )

  function isFeatureEnabled(key: keyof ClientFeatures): boolean {
    return config.value?.features[key] ?? false
  }

  function getSubjectLabel(key: string): string {
    return subjects.value.find((s) => s.key === key)?.label ?? key
  }

  function getSeasonLabel(id: string): string {
    return seasons.value.find((s) => s.id === id)?.label ?? id
  }

  return {
    initialize,
    fetchSeasons,
    branding,
    subjects,
    seasons,
    features,
    links,
    locale,
    watermarkConfig,
    highlightsConfig,
    seasonSubjectAvailability,
    subjectOptions,
    seasonOptions,
    isFeatureEnabled,
    getSubjectLabel,
    getSeasonLabel,
  }
})
